/**
 * User Administration Service
 * Provides API calls for admin user management operations
 */

/**
 * Get the admin API base URL
 * In development: uses proxy (/api/admin)
 * In production: uses full Cloud Function URL
 */
function getAdminApiUrl(): string {
    const apiUrl = import.meta.env.VITE_ADMIN_API_URL;
    if (apiUrl) {
        return apiUrl;
    }
    // Fallback to proxy for development
    return '/api/admin';
}

const API_BASE_URL = `${getAdminApiUrl()}/admin`;

export interface UserListItem {
    uid: string;
    email: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    subscriptionTier: string;
    subscriptionStatus: string;
    createdAt: string;
    lastLoginAt?: string;
    emailVerified: boolean;
    disabled: boolean;
}

export interface PaginatedUsers {
    users: UserListItem[];
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
}

export interface UserFilters {
    searchQuery?: string;
    subscriptionTier?: string;
    subscriptionStatus?: string;
    emailVerified?: boolean;
    disabled?: boolean;
    sortBy?: 'createdAt' | 'lastLoginAt' | 'email';
    sortOrder?: 'asc' | 'desc';
}

/**
 * Get authorization token for admin API calls
 */
async function getAuthToken(): Promise<string> {
    const { getAdminJWT } = await import('./adminAuthService');
    return await getAdminJWT();
}

/**
 * Make an authenticated API request
 */
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers,
        },
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `API request failed: ${response.statusText}`);
    }

    return response.json();
}

/**
 * List users with pagination and filtering
 */
export async function listUsers(
    page: number = 1,
    pageSize: number = 50,
    filters: UserFilters = {}
): Promise<PaginatedUsers> {
    const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...(filters.searchQuery && { search: filters.searchQuery }),
        ...(filters.subscriptionTier && { tier: filters.subscriptionTier }),
        ...(filters.subscriptionStatus && { status: filters.subscriptionStatus }),
        ...(filters.emailVerified !== undefined && { emailVerified: filters.emailVerified.toString() }),
        ...(filters.disabled !== undefined && { disabled: filters.disabled.toString() }),
        ...(filters.sortBy && { sortBy: filters.sortBy }),
        ...(filters.sortOrder && { sortOrder: filters.sortOrder }),
    });

    return apiRequest<PaginatedUsers>(`/users?${params.toString()}`);
}

/**
 * Search users by email or name (debounced on client side)
 */
export async function searchUsers(query: string, limit: number = 20): Promise<UserListItem[]> {
    if (!query || query.trim().length < 2) {
        return [];
    }

    const params = new URLSearchParams({
        search: query.trim(),
        limit: limit.toString(),
    });

    const result = await apiRequest<{ users: UserListItem[] }>(`/users/search?${params.toString()}`);
    return result.users;
}

/**
 * Get detailed user information
 */
export async function getUserDetails(uid: string): Promise<any> {
    return apiRequest<any>(`/users/${uid}`);
}

/**
 * Update user account status (enable/disable)
 */
export async function updateUserStatus(
    uid: string,
    disabled: boolean,
    reason: string
): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>(`/users/${uid}/status`, {
        method: 'PUT',
        body: JSON.stringify({ disabled, reason }),
    });
}

/**
 * Reset user password (send email)
 */
export async function sendPasswordResetEmail(email: string): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>(`/users/password-reset`, {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
}

/**
 * Delete user account (soft delete - marks as disabled)
 */
export async function deleteUser(uid: string, reason: string): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>(`/users/${uid}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason }),
    });
}

/**
 * Export users to CSV
 */
export async function exportUsers(filters: UserFilters = {}): Promise<Blob> {
    const token = await getAuthToken();

    const params = new URLSearchParams({
        ...(filters.searchQuery && { search: filters.searchQuery }),
        ...(filters.subscriptionTier && { tier: filters.subscriptionTier }),
        ...(filters.subscriptionStatus && { status: filters.subscriptionStatus }),
    });

    const response = await fetch(`${API_BASE_URL}/users/export?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error('Failed to export users');
    }

    return response.blob();
}

/**
 * Get user statistics
 */
export async function getUserStats(): Promise<{
    totalUsers: number;
    activeUsers: number;
    newUsersThisMonth: number;
    bySubscriptionTier: Record<string, number>;
    byStatus: Record<string, number>;
}> {
    return apiRequest<any>('/users/stats');
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

/**
 * Format relative time (e.g., "2 days ago")
 */
export function formatRelativeTime(dateString: string): string {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        if (diffHours === 0) {
            const diffMins = Math.floor(diffMs / (1000 * 60));
            return diffMins === 0 ? 'Just now' : `${diffMins}m ago`;
        }
        return `${diffHours}h ago`;
    } else if (diffDays === 1) {
        return 'Yesterday';
    } else if (diffDays < 7) {
        return `${diffDays}d ago`;
    } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks}w ago`;
    } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months}mo ago`;
    } else {
        const years = Math.floor(diffDays / 365);
        return `${years}y ago`;
    }
}

/**
 * Get subscription tier badge color
 */
export function getSubscriptionTierColor(tier: string): string {
    const colors: Record<string, string> = {
        free: 'bg-gray-100 text-gray-800 border-gray-300',
        starter: 'bg-blue-100 text-blue-800 border-blue-300',
        pro: 'bg-purple-100 text-purple-800 border-purple-300',
        executive: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    };
    return colors[tier.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-300';
}

/**
 * Get subscription status badge color
 */
export function getSubscriptionStatusColor(status: string): string {
    const colors: Record<string, string> = {
        active: 'bg-green-100 text-green-800 border-green-300',
        canceled: 'bg-red-100 text-red-800 border-red-300',
        past_due: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        incomplete: 'bg-orange-100 text-orange-800 border-orange-300',
        trialing: 'bg-blue-100 text-blue-800 border-blue-300',
    };
    return colors[status.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-300';
}
